import { AlertCircle, Check, ChevronRight, Database, Heart, Loader2, Zap } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUpdateApiKey, useUserCredentials } from '../../api/hooks/useCredentials'
import { useUserCSVUpload } from '../../api/hooks/useCSVStorage'
import {
  useUpdateAppSettings,
  useUpdateUserSettings,
  useUserSettings
} from '../../api/hooks/useSettings'
import { DataImportSection } from '../../components/DataImportSection'
import { Card } from '../../components/ui/card'
import { SectionHeader } from '../../components/ui/section-header'
import { UnifiedButton } from '../../components/ui/unified-button'
import { WithingsConnection } from '../../components/WithingsConnection'
import { useAuth } from '../../stores/authStore'
import { validateApiKey } from '../../utils'

type SetupStep = 'hevy' | 'withings'

export default function SetupPage() {
  const navigate = useNavigate()
  const { user } = useAuth()

  // Server state hooks
  const { data: userSettings } = useUserSettings()
  const { data: userCredentials } = useUserCredentials()
  const { data: csvUpload } = useUserCSVUpload()
  const updateSettings = useUpdateUserSettings()
  const updateApiKey = useUpdateApiKey()
  const updateAppSettings = useUpdateAppSettings()

  const [step, setStep] = useState<SetupStep>('hevy')
  const [localApiKey, setLocalApiKey] = useState('')
  const [localDataSource, setLocalDataSource] = useState<'api' | 'csv'>('api')
  const [_showApiKey, _setShowApiKey] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isValidating, setIsValidating] = useState(true)

  // fire once
  const hasMarked = useRef(false)

  // Check if user is authenticated
  useEffect(() => {
    // wait until we actually have settings from the server
    if (!user || !userSettings) return

    if (!userSettings.userSeenSetup && !hasMarked.current) {
      hasMarked.current = true
      updateSettings.mutate({ userSeenSetup: true })
    }
  }, [user, userSettings, updateSettings])

  // Check existing API key and pre-fill if it exists
  useEffect(() => {
    if (!user) return

    setIsValidating(true)

    // Read from server state
    if (userCredentials?.apiKey) {
      setLocalApiKey(userCredentials.apiKey)
      setStep('withings')
    }

    // Initialize local data source from server state
    if (userSettings?.settings?.dataSource) {
      setLocalDataSource(userSettings.settings.dataSource)
    }

    setIsValidating(false)
  }, [user, userCredentials?.apiKey, userSettings?.settings?.dataSource])

  const isApiKeyValid = localApiKey ? validateApiKey(localApiKey) : false

  const handleHevySubmit = async () => {
    setError(null)
    setIsLoading(true)

    try {
      if (localDataSource === 'api' && !isApiKeyValid) {
        throw new Error('Please enter a valid API key')
      }

      // Save API key only if using API data source
      if (localDataSource === 'api') {
        await updateApiKey.mutateAsync(localApiKey)
      }

      // Save the selected data source
      await updateAppSettings.mutateAsync({ dataSource: localDataSource })

      // Move to Withings step
      setStep('withings')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setIsLoading(false)
    }
  }

  const _handleCsvUpload = async (_file: File) => {
    setError(null)
    setIsLoading(true)

    try {
      // TODO: Implement CSV upload with new storage hooks
      // For now, just set data source to CSV
      await updateAppSettings.mutateAsync({ dataSource: 'csv' })

      // Set placeholder in credentials to indicate CSV setup is done
      await updateApiKey.mutateAsync('CSV_IMPORT')

      // Move to Withings step
      setStep('withings')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process CSV file')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSkipWithings = () => {
    // Go to dashboard (setup status already marked as seen)
    navigate('/')
  }

  const handleSkipHevy = async () => {
    setError(null)
    setIsLoading(true)

    try {
      // Save the selected data source even when skipping
      await updateAppSettings.mutateAsync({ dataSource: localDataSource })

      // Move to Withings step
      setStep('withings')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setIsLoading(false)
    }
  }

  if (isValidating) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <Loader2 className="text-primary h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="bg-background flex min-h-screen items-center justify-center p-4">
      <div className="mx-auto max-w-4xl flex-1">
        {/* Skip Setup Button - Top Right */}
        <div className="absolute top-4 right-4">
          <UnifiedButton
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-muted-foreground hover:text-foreground"
          >
            Skip setup →
          </UnifiedButton>
        </div>

        {/* Logo and Progress */}
        <div className="mb-8 space-y-6">
          {/* Logo */}
          <div className="flex justify-center">
            <div className="flex items-center space-x-2">
              <Zap className="h-8 w-8 text-orange-500" fill="currentColor" />
              <span className="text-2xl font-bold tracking-tight">Loadline</span>
            </div>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center justify-center space-x-4">
            <div className="flex items-center">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full ${
                  step === 'hevy' ? 'bg-primary text-primary-foreground' : 'bg-green-500 text-white'
                }`}
              >
                {step === 'hevy' ? '1' : <Check className="h-5 w-5" />}
              </div>
              <span className="ml-2 font-medium">Workout Data</span>
            </div>

            <ChevronRight className="text-muted-foreground h-5 w-5" />

            <div className="flex items-center">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full ${
                  step === 'withings'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                2
              </div>
              <span className="text-muted-foreground ml-2 font-medium">Weight Tracking</span>
            </div>
          </div>
        </div>

        {step === 'hevy' && (
          <Card className="mx-auto flex-1 p-6">
            <SectionHeader icon={Database} title="Connect Your Workout Data" />
            <p className="text-muted-foreground mt-1 text-sm">
              Choose how to import your workout data from Hevy
            </p>
            <p className="text-muted-foreground mt-1 mb-6 text-xs">
              This step is optional - you can skip and add data later
            </p>

            <div className="space-y-6">
              {error && (
                <div className="text-destructive bg-destructive/10 flex items-center gap-2 rounded-md p-3 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Data Import Section */}
              <DataImportSection
                dataSource={localDataSource}
                apiKey={localApiKey}
                onDataSourceChange={setLocalDataSource}
                onApiKeyChange={setLocalApiKey}
                dataSourceToggleTitle="Import Method"
                dataSourceToggleDescription="Live sync for automatic updates or import from CSV file"
              />

              {/* Action Buttons */}
              <div className="mt-4 flex items-center justify-end gap-1">
                <UnifiedButton
                  variant="outline"
                  onClick={handleSkipHevy}
                  disabled={isLoading}
                  size="md"
                  className="w-36"
                >
                  Skip for now
                </UnifiedButton>
                {(localDataSource === 'api' && isApiKeyValid) ||
                (localDataSource === 'csv' && csvUpload) ? (
                  <UnifiedButton
                    onClick={handleHevySubmit}
                    disabled={isLoading}
                    className="h-9 w-36"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Continue'
                    )}
                  </UnifiedButton>
                ) : null}
              </div>
            </div>
          </Card>
        )}

        {step === 'withings' && (
          <Card className="mx-auto max-w-xl p-6">
            <SectionHeader icon={Heart} title="Connect Your Weight Data" />
            <p className="text-muted-foreground mt-1 text-sm">
              Sync your weight and body composition automatically
            </p>
            <p className="text-muted-foreground mt-1 mb-6 text-xs">
              This step is optional - you can skip and connect later
            </p>

            <div className="space-y-6">
              <div className="bg-primary/5 border-primary/20 rounded-lg border p-4">
                <div className="flex gap-3">
                  <div className="space-y-2">
                    <p className="font-medium">Why connect Withings?</p>
                    <ul className="text-muted-foreground space-y-1 text-sm">
                      <li>• Automatic weight tracking with your workouts</li>
                      <li>• Body composition analysis and trends</li>
                      <li>• See how your training affects your physique</li>
                      <li>• No manual data entry needed</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-1">
                <UnifiedButton
                  onClick={handleSkipWithings}
                  variant="outline"
                  size="md"
                  className="w-36"
                >
                  Skip for now
                </UnifiedButton>
                <WithingsConnection
                  showAsRow={false}
                  buttonVariant="primary"
                  buttonSize="md"
                  className="h-9"
                />
              </div>

              <p className="text-muted-foreground text-center text-xs">
                You can always connect Withings later from your settings
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}
